import React, { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { DynamicPictogramName, PictogramProps } from './types'
import { mapPictogramName } from './utils/PictogramMapper'

interface DynamicPictogramProps extends PictogramProps {
  pictogramName: DynamicPictogramName
  fallback?: React.ReactNode
  loadingFallback?: React.ReactNode
  lazyLoad?: boolean
  rootMargin?: string
  threshold?: number | number[]
  wrapperClassName?: string
}

export const DynamicPictogram: React.FC<DynamicPictogramProps> = ({
  pictogramName,
  fallback = null,
  loadingFallback = null,
  lazyLoad = true,
  rootMargin = '100px',
  threshold = 0,
  wrapperClassName,
  ...props
}) => {
  const [PictogramComponent, setPictogramComponent] = useState<React.ComponentType<PictogramProps> | null>(null)
  const [isLoading, setIsLoading] = useState(lazyLoad)
  const [error, setError] = useState<Error | null>(null)
  const [isVisible, setIsVisible] = useState(!lazyLoad)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!lazyLoad) {
      setIsVisible(true)
      return
    }

    const currentRef = containerRef.current
    if (!currentRef) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin, threshold }
    )

    observer.observe(currentRef)

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [lazyLoad, rootMargin, threshold])

  useEffect(() => {
    if (!isVisible) return

    const mappedName = mapPictogramName(pictogramName)

    setIsLoading(true)
    setError(null)
    setPictogramComponent(null)

    const importPictogram = async () => {
      try {
        const module = await import(`./pictogram-components/${mappedName}.tsx`)
        const Component = module.default || module[mappedName]

        if (Component) {
          setPictogramComponent(() => Component)
        } else {
          throw new Error(`Pictogram component not found in module: ${mappedName}`)
        }
      } catch (err) {
        console.warn(`Failed to load pictogram: ${mappedName}`, err)
        setError(err instanceof Error ? err : new Error(String(err)))
      } finally {
        setIsLoading(false)
      }
    }

    importPictogram()
  }, [pictogramName, isVisible])

  return (
    <div ref={containerRef} className={cn('inline-block', wrapperClassName)}>
      {isLoading ? loadingFallback : error || !PictogramComponent ? fallback : <PictogramComponent {...props} />}
    </div>
  )
}
